import type { EdgeSeries } from '@projections/data';
import type { Rng } from './rng';
import type { MatrixReturn, ReturnData, ReturnInput, Sampler } from './sampler';

/**
 * M3 samplers (PLAN.md §2c) — block bootstrap, historical full sequences, and CORRELATED
 * multi-asset bootstrap, plus the M1→engine BRIDGE that turns canonical `@projections/data`
 * series into the {@link ReturnData} cross-section the samplers draw from.
 *
 * DESIGN CONTRACT (this file is the M3 DESIGN agent's published surface; the M3 BUILD agents
 * fill the bodies):
 *
 *  - Every sampler implements {@link Sampler} from `./sampler`:
 *      `sample(data: ReturnData, horizon: number, nPaths: number, rng: Rng): ReturnInput[]`.
 *    It is PURE w.r.t. its inputs: same `data` + same seeded `rng` -> identical `ReturnInput[]`.
 *    The deterministic core (`simulatePath`) is seed-free; ALL randomness comes from `rng`.
 *
 *  - The IID scalar bootstrap already shipped in `./sampler` (`IidBootstrapSampler`, emits the
 *    `sequence` form from `data.annual`). The M3 samplers below add (a) block & historical
 *    SEQUENCE preservation and (b) MULTI-ASSET correlation via whole-row draws.
 *
 *  - CORRELATION FOR FREE (PLAN.md §2c). The multi-asset samplers draw whole HISTORICAL-YEAR
 *    CROSS-SECTIONS: one draw of index `t` takes `data.rows[t]` — that year's stock + bond +
 *    global + cash (+ inflation + FX) JOINTLY — so stock/bond/inflation/FX correlation is
 *    preserved without estimating a covariance matrix. Each produced `ReturnInput` is the
 *    `matrix` form ({@link MatrixReturn}) whose `assetClasses === data.assetClasses` and whose
 *    `byYear[y]` is a drawn `data.rows[t]`; `simulatePath` allocation-weights it per account.
 *
 *  - SEQUENCE-OF-RETURNS STRESS (worst-ordering, retire-into-YYYY) is **M5**, NOT here.
 */

// ---------------------------------------------------------------------------
// IID multi-asset bootstrap (correlated, whole-row, per-year independent draws)
// ---------------------------------------------------------------------------

/**
 * IID CORRELATED multi-asset bootstrap. For each simulated year it draws ONE historical row
 * `data.rows[t]` (uniform with replacement over `t`) and uses that whole cross-section as the
 * year's per-asset returns. Independent across years (destroys autocorrelation by design — use
 * {@link BlockBootstrapSampler} to keep it) but preserves CROSS-ASSET correlation within each
 * year because the stock/bond/global/cash returns come from the SAME historical year.
 *
 * Emits the `matrix` {@link ReturnInput} form: `assetClasses === data.assetClasses`, and
 * `byYear[y]` is the drawn `data.rows[t]`. Requires `data.assetClasses` + `data.rows`
 * (throws if absent or misaligned). This is the multi-asset analogue of the scalar
 * `IidBootstrapSampler` in `./sampler`.
 */
export class IidMultiAssetSampler implements Sampler {
  sample(data: ReturnData, horizon: number, nPaths: number, rng: Rng): ReturnInput[] {
    validateReturnData(data);
    const poolSize = (data.rows as ReadonlyArray<readonly number[]>).length;
    const paths: ReturnInput[] = [];
    for (let p = 0; p < nPaths; p++) {
      const draws = new Array<number>(horizon);
      for (let y = 0; y < horizon; y++) {
        // One independent whole-row draw per simulated year (with replacement).
        draws[y] = rng.nextInt(poolSize);
      }
      paths.push(matrixFromDraws(data, draws));
    }
    return paths;
  }
}

// ---------------------------------------------------------------------------
// Block bootstrap (preserves autocorrelation / momentum)
// ---------------------------------------------------------------------------

/** Tuning for {@link BlockBootstrapSampler}. */
export interface BlockBootstrapOptions {
  /**
   * Fixed block length in periods (e.g. 5 historical years per block). Each path is built by
   * concatenating randomly-placed blocks of consecutive historical rows until the horizon is
   * covered, preserving within-block autocorrelation / momentum that IID destroys.
   */
  readonly blockLength: number;
  /**
   * Wrap blocks circularly past the end of history (CIRCULAR block bootstrap) when `true`
   * (default), so every start index is equally likely; when `false`, block starts are limited
   * to `[0, n - blockLength]` (no wrap).
   */
  readonly circular?: boolean;
}

/**
 * BLOCK bootstrap. Resamples multi-year BLOCKS of consecutive historical rows (length
 * `options.blockLength`) with replacement, concatenating them to cover the horizon, so
 * autocorrelation / momentum within a block survives (cf. IID, which destroys it). Operates on
 * the multi-asset `data.rows` cross-section (so cross-asset correlation is preserved too) and
 * emits the `matrix` form.
 *
 * Deterministic for a fixed seed: every block START INDEX is drawn from `rng`.
 *
 * CIRCULAR (default): a block starting at `s` is `rows[s], rows[(s+1)%n], …` — it wraps past
 * the end of history, so every start index `s ∈ [0, n)` is equally likely (Politis–Romano
 * circular block bootstrap). NON-CIRCULAR (`circular: false`): start indices are confined to
 * `[0, n - blockLength]` so a block never runs off the end (requires `n >= blockLength`).
 */
export class BlockBootstrapSampler implements Sampler {
  readonly options: BlockBootstrapOptions;
  constructor(options: BlockBootstrapOptions) {
    if (!Number.isInteger(options.blockLength) || options.blockLength < 1) {
      throw new Error(
        `BlockBootstrapSampler: blockLength must be a positive integer, got ${options.blockLength}`,
      );
    }
    this.options = options;
  }

  sample(data: ReturnData, horizon: number, nPaths: number, rng: Rng): ReturnInput[] {
    validateReturnData(data);
    const n = (data.rows as ReadonlyArray<readonly number[]>).length;
    const { blockLength } = this.options;
    const circular = this.options.circular ?? true;

    // Number of distinct start indices a block may take.
    let startCount: number;
    if (circular) {
      startCount = n; // every index can start a (wrapping) block
    } else {
      if (n < blockLength) {
        throw new Error(
          `BlockBootstrapSampler: non-circular blockLength ${blockLength} exceeds history length ${n}`,
        );
      }
      startCount = n - blockLength + 1;
    }

    const paths: ReturnInput[] = [];
    for (let p = 0; p < nPaths; p++) {
      const draws = new Array<number>(horizon);
      let filled = 0;
      while (filled < horizon) {
        const start = rng.nextInt(startCount);
        // Lay down one block of consecutive rows; stop early once the horizon is covered.
        for (let b = 0; b < blockLength && filled < horizon; b++) {
          // Circular wraps past the end; non-circular stays within bounds by construction.
          draws[filled] = circular ? (start + b) % n : start + b;
          filled++;
        }
      }
      paths.push(matrixFromDraws(data, draws));
    }
    return paths;
  }
}

// ---------------------------------------------------------------------------
// Historical full sequences (cFIREsim-style backtest — deterministic, not MC)
// ---------------------------------------------------------------------------

/** Tuning for {@link HistoricalSequenceSampler}. */
export interface HistoricalSequenceOptions {
  /**
   * What to do when a rolling start year cannot supply a full `horizon` of consecutive rows.
   *  - `'drop'`   (default): omit truncated windows entirely (cFIREsim default) — the produced
   *               path count is then `max(0, rows.length - horizon + 1)`, NOT `nPaths`.
   *  - `'wrap'`   wrap circularly so every start year yields a full-length window.
   */
  readonly partial?: 'drop' | 'wrap';
}

/**
 * HISTORICAL FULL SEQUENCES (a cFIREsim-style backtest, the Trinity-style validation driver).
 * NOT Monte Carlo: it does NOT consume `rng` for selection. It enumerates every rolling start
 * index `t0` in history and emits the contiguous window `data.rows[t0 .. t0+horizon-1]` as one
 * `matrix` `ReturnInput`. With `n` rolling windows it produces (per `partial`) up to `n` paths
 * REGARDLESS of the requested `nPaths` (the caller passes the windows it wants; `nPaths` is a
 * cap/ignored — documented so the validation gate's path count is deterministic). This is the
 * sampler the Trinity / Portfolio Visualizer mechanics gates run through (4%/30yr/50-50 on US
 * sequences ≈ 95% success).
 *
 *  - `'drop'` (default): start indices `t0 ∈ [0, n - horizon]` so every window is a full,
 *    in-bounds slice of real history → `max(0, n - horizon + 1)` paths.
 *  - `'wrap'`: start indices `t0 ∈ [0, n)`, wrapping circularly past the end so EVERY start
 *    year yields a full-length window → `n` paths.
 */
export class HistoricalSequenceSampler implements Sampler {
  readonly options: HistoricalSequenceOptions;
  constructor(options: HistoricalSequenceOptions = {}) {
    this.options = options;
  }

  sample(data: ReturnData, horizon: number, _nPaths: number, _rng: Rng): ReturnInput[] {
    validateReturnData(data);
    const n = (data.rows as ReadonlyArray<readonly number[]>).length;
    const wrap = this.options.partial === 'wrap';

    const paths: ReturnInput[] = [];
    if (wrap) {
      // Every start index yields a full-length window, wrapping past the end of history.
      for (let t0 = 0; t0 < n; t0++) {
        const draws = new Array<number>(horizon);
        for (let y = 0; y < horizon; y++) {
          draws[y] = (t0 + y) % n;
        }
        paths.push(matrixFromDraws(data, draws));
      }
    } else {
      // Drop truncated tail windows: only in-bounds full slices of real history.
      const windowCount = Math.max(0, n - horizon + 1);
      for (let t0 = 0; t0 < windowCount; t0++) {
        const draws = new Array<number>(horizon);
        for (let y = 0; y < horizon; y++) {
          draws[y] = t0 + y;
        }
        paths.push(matrixFromDraws(data, draws));
      }
    }
    return paths;
  }
}

// ---------------------------------------------------------------------------
// M1 → engine bridge: canonical series  ->  ReturnData cross-section
// ---------------------------------------------------------------------------

/**
 * How one engine asset class maps onto canonical `@projections/data` series for the bridge.
 * The bridge reads each `seriesId`'s edge projection, converts it to a per-period DECIMAL
 * return (a `return_pct` series is /100; an `index_level` series is differenced into returns),
 * aligns all classes on a common period grid, and stacks them into the {@link ReturnData}
 * matrix in `assetClasses` order.
 */
export interface AssetClassMapping {
  /** Engine asset-class id (the {@link ReturnData} column + account `allocation` key). */
  readonly assetClass: string;
  /** Canonical series_id supplying this class's returns, e.g. `th_set_tr`, `global_acwi_tr_thb`. */
  readonly seriesId: string;
}

/** Options for {@link buildReturnData}. */
export interface BuildReturnDataOptions {
  /** Asset-class → series mapping; defines the matrix column order (`assetClasses`). */
  readonly mappings: readonly AssetClassMapping[];
  /** Optional inflation series_id (e.g. `th_cpi`) → aligned `ReturnData.inflation` column. */
  readonly inflationSeriesId?: string;
  /** Optional FX series_id (e.g. `th_fx_thb_usd`) → aligned `ReturnData.fx` column. */
  readonly fxSeriesId?: string;
  /**
   * Target frequency of the cross-section. `'annual'` (default) compounds monthly returns into
   * calendar-year returns before stacking (Trinity-comparable); `'monthly'` keeps months.
   */
  readonly frequency?: 'annual' | 'monthly';
  /**
   * Which `annual` (scalar, back-compat) column to also fill, by asset-class id. If omitted the
   * bridge MAY fill `ReturnData.annual` from an equal-weight blend of the mapped classes so the
   * legacy `IidBootstrapSampler` keeps working off the same bundle.
   */
  readonly annualFromAssetClass?: string;
}

/** One series turned into per-period decimal returns keyed by its period label. */
interface ReturnSeries {
  /** Ordered period labels (`YYYY` or `YYYY-MM`) of the return observations. */
  readonly periods: readonly string[];
  /** `valueByPeriod.get(period)` = the decimal return for that period. */
  readonly valueByPeriod: ReadonlyMap<string, number>;
}

/**
 * THE M1 → ENGINE BRIDGE. Turn canonical edge series (the compact `{ meta, dates[], values[] }`
 * projection the data layer exports, `EdgeSeries`) into the multi-asset {@link ReturnData}
 * cross-section the M3 samplers draw from. Steps:
 *
 *  1. Look each mapped `seriesId` up in `seriesById`; convert to per-period DECIMAL returns
 *     (`return_pct`/100, or `index_level` → period-over-period return; first obs has no return).
 *  2. INNER-JOIN all mapped classes (+ inflation + FX) on their common period grid so every
 *     row is contemporaneous (this contemporaneity is what preserves correlation on a draw).
 *  3. If `frequency: 'annual'`, compound the joined monthly returns into calendar-year rows.
 *  4. Stack into `rows[t][a]` in `mappings` order; fill `periods`, optional `inflation`/`fx`,
 *     and a back-compat `annual` column (per `annualFromAssetClass` or an equal-weight blend).
 *  5. VALIDATE the {@link ReturnData} alignment invariant and throw on violation.
 *
 * PURE: no I/O — the caller (research app / API / test) loads the `EdgeSeries`es (offline from a
 * fixture, or from KV in the Worker) and hands them in as a `Map`. Same inputs -> same output.
 */
export function buildReturnData(
  seriesById: ReadonlyMap<string, EdgeSeries>,
  options: BuildReturnDataOptions,
): ReturnData {
  const { mappings } = options;
  if (mappings.length === 0) {
    throw new Error('buildReturnData: at least one asset-class mapping is required');
  }
  const assetClasses = mappings.map((m) => m.assetClass);
  const seen = new Set<string>();
  for (const ac of assetClasses) {
    if (seen.has(ac)) {
      throw new Error(`buildReturnData: duplicate assetClass "${ac}" in mappings`);
    }
    seen.add(ac);
  }
  const frequency = options.frequency ?? 'annual';

  // 1. Convert every mapped series (+ inflation + FX) into per-period decimal returns at the
  //    requested frequency.
  const lookup = (seriesId: string, role: string): EdgeSeries => {
    const edge = seriesById.get(seriesId);
    if (edge === undefined) {
      throw new Error(`buildReturnData: ${role} series "${seriesId}" not found in seriesById`);
    }
    return edge;
  };

  const classReturns = mappings.map((m) =>
    toReturnSeries(lookup(m.seriesId, `asset-class "${m.assetClass}"`), frequency),
  );
  const inflationReturns =
    options.inflationSeriesId === undefined
      ? undefined
      : toReturnSeries(lookup(options.inflationSeriesId, 'inflation'), frequency);
  const fxReturns =
    options.fxSeriesId === undefined
      ? undefined
      : toReturnSeries(lookup(options.fxSeriesId, 'fx'), frequency);

  // 2. Inner-join on the common period grid: the intersection of every contributing series'
  //    periods, in chronological order (contemporaneity == correlation-preserving).
  const contributors: ReturnSeries[] = [...classReturns];
  if (inflationReturns !== undefined) {
    contributors.push(inflationReturns);
  }
  if (fxReturns !== undefined) {
    contributors.push(fxReturns);
  }
  const commonPeriods = intersectPeriods(contributors);
  if (commonPeriods.length === 0) {
    throw new Error('buildReturnData: no overlapping periods across the mapped series');
  }

  // 3. (Compounding into calendar-year rows already happened inside toReturnSeries when
  //    frequency === 'annual'; here we just stack the joined grid.)

  // 4. Stack rows[t][a] in mappings order, plus aligned inflation/fx columns.
  const rows: number[][] = commonPeriods.map((period) =>
    classReturns.map((cr) => cr.valueByPeriod.get(period) as number),
  );
  const inflation =
    inflationReturns === undefined
      ? undefined
      : commonPeriods.map((period) => inflationReturns.valueByPeriod.get(period) as number);
  const fx =
    fxReturns === undefined
      ? undefined
      : commonPeriods.map((period) => fxReturns.valueByPeriod.get(period) as number);

  // Back-compat `annual` scalar column: a single representative sleeve so the legacy
  // `IidBootstrapSampler` keeps working off the same bundle. Either a named asset class or an
  // equal-weight blend of every mapped class.
  const annual = backCompatAnnual(rows, assetClasses, options.annualFromAssetClass);

  const data: ReturnData = {
    annual,
    assetClasses,
    periods: commonPeriods,
    rows,
    ...(inflation !== undefined ? { inflation } : {}),
    ...(fx !== undefined ? { fx } : {}),
  };

  // 5. Validate the alignment invariant.
  return validateReturnData(data);
}

/**
 * Convert one {@link EdgeSeries} to per-period DECIMAL returns at the requested `frequency`.
 *  - `return_pct` values are decimalised (`/100`).
 *  - `index_level` values become period-over-period returns (`level[t]/level[t-1] - 1`); the
 *    first observation has no return row.
 *  - `yield_pct` is rejected: yields are not investable returns (bond TR is pre-synthesized as a
 *    `return_pct` series by the data layer).
 * When `frequency === 'annual'` and the series is monthly, the per-month returns are compounded
 * into calendar-year returns keyed by `'YYYY'` (only full 12-month years are kept).
 */
function toReturnSeries(edge: EdgeSeries, frequency: 'annual' | 'monthly'): ReturnSeries {
  const { meta, dates, values } = edge;
  if (dates.length !== values.length) {
    throw new Error(
      `buildReturnData: series "${meta.series_id}" has ${dates.length} dates but ${values.length} values`,
    );
  }

  // Per-observation decimal returns keyed by the source period label.
  const obsPeriods: string[] = [];
  const obsReturns: number[] = [];
  if (meta.unit === 'return_pct') {
    for (let i = 0; i < dates.length; i++) {
      obsPeriods.push(dates[i] as string);
      obsReturns.push((values[i] as number) / 100);
    }
  } else if (meta.unit === 'index_level') {
    // Period-over-period return; the first observation has no prior level, so no return row.
    for (let i = 1; i < dates.length; i++) {
      const prev = values[i - 1] as number;
      const cur = values[i] as number;
      if (prev === 0) {
        throw new Error(
          `buildReturnData: series "${meta.series_id}" has a zero index level at ${dates[i - 1]}`,
        );
      }
      obsPeriods.push(dates[i] as string);
      obsReturns.push(cur / prev - 1);
    }
  } else {
    throw new Error(
      `buildReturnData: series "${meta.series_id}" unit "${meta.unit}" is not an investable return` +
        ' (yields are not returns; the data layer pre-synthesizes bond TR as return_pct)',
    );
  }

  // Calendar-year compounding when targeting annual frequency from a monthly series.
  if (frequency === 'annual' && meta.frequency === 'monthly') {
    return compoundToAnnual(obsPeriods, obsReturns, meta.series_id);
  }

  // Already at the target frequency (or annual source). Key by year-only label so an annual
  // series labelled 'YYYY' or 'YYYY-MM' both align with the annual grid.
  const valueByPeriod = new Map<string, number>();
  const periods: string[] = [];
  for (let i = 0; i < obsPeriods.length; i++) {
    const label =
      frequency === 'annual' ? yearOf(obsPeriods[i] as string) : (obsPeriods[i] as string);
    if (valueByPeriod.has(label)) {
      throw new Error(
        `buildReturnData: series "${meta.series_id}" has duplicate period "${label}" at annual frequency`,
      );
    }
    valueByPeriod.set(label, obsReturns[i] as number);
    periods.push(label);
  }
  return { periods, valueByPeriod };
}

/**
 * Compound monthly decimal returns into calendar-year returns. Only full 12-month years are
 * kept (partial leading / trailing years are dropped — Trinity-comparable full-year rows).
 */
function compoundToAnnual(
  monthPeriods: readonly string[],
  monthReturns: readonly number[],
  seriesId: string,
): ReturnSeries {
  const byYear = new Map<string, number[]>();
  for (let i = 0; i < monthPeriods.length; i++) {
    const year = yearOf(monthPeriods[i] as string);
    const bucket = byYear.get(year);
    if (bucket === undefined) {
      byYear.set(year, [monthReturns[i] as number]);
    } else {
      bucket.push(monthReturns[i] as number);
    }
  }
  const periods: string[] = [];
  const valueByPeriod = new Map<string, number>();
  // Years sorted ascending so the joined grid stays chronological.
  for (const year of [...byYear.keys()].sort()) {
    const months = byYear.get(year) as number[];
    if (months.length !== 12) {
      continue; // drop partial years
    }
    let compounded = 1;
    for (const r of months) {
      compounded *= 1 + r;
    }
    periods.push(year);
    valueByPeriod.set(year, compounded - 1);
  }
  if (periods.length === 0) {
    throw new Error(
      `buildReturnData: series "${seriesId}" has no full 12-month calendar year after compounding`,
    );
  }
  return { periods, valueByPeriod };
}

/** The 4-digit year label of a `'YYYY'` or `'YYYY-MM'` period string. */
function yearOf(period: string): string {
  return period.slice(0, 4);
}

/**
 * Inner-join: the chronologically-ordered set of period labels present in EVERY contributing
 * series. Order follows the first contributor's period order, filtered to the intersection.
 */
function intersectPeriods(contributors: readonly ReturnSeries[]): string[] {
  const first = contributors[0];
  if (first === undefined) {
    return [];
  }
  const out: string[] = [];
  for (const period of first.periods) {
    let inAll = true;
    for (let c = 1; c < contributors.length; c++) {
      if (!(contributors[c] as ReturnSeries).valueByPeriod.has(period)) {
        inAll = false;
        break;
      }
    }
    if (inAll) {
      out.push(period);
    }
  }
  return out;
}

/**
 * The back-compat scalar `annual` column. With `annualFromAssetClass` set, it echoes that
 * class's per-period returns; otherwise an equal-weight blend across all mapped classes.
 */
function backCompatAnnual(
  rows: readonly (readonly number[])[],
  assetClasses: readonly string[],
  annualFromAssetClass: string | undefined,
): number[] {
  if (annualFromAssetClass !== undefined) {
    const a = assetClasses.indexOf(annualFromAssetClass);
    if (a < 0) {
      throw new Error(
        `buildReturnData: annualFromAssetClass "${annualFromAssetClass}" is not a mapped asset class`,
      );
    }
    return rows.map((row) => row[a] as number);
  }
  const k = assetClasses.length;
  return rows.map((row) => {
    let s = 0;
    for (let a = 0; a < k; a++) {
      s += row[a] as number;
    }
    return s / k;
  });
}

/**
 * Validate (and return) a {@link ReturnData} against the multi-asset alignment invariant
 * documented on the interface: rectangular `rows` matching `assetClasses.length`, `periods`
 * length matching `rows`, aligned optional `inflation`/`fx`, distinct asset classes, ≥1 row.
 * Throws on violation. The matrix samplers call this before drawing so a misaligned bundle
 * fails loudly rather than silently mis-correlating.
 */
export function validateReturnData(data: ReturnData): ReturnData {
  const { assetClasses, rows, periods, inflation, fx } = data;
  if (assetClasses === undefined || rows === undefined) {
    throw new Error('validateReturnData: multi-asset data requires both `assetClasses` and `rows`');
  }
  if (assetClasses.length === 0) {
    throw new Error('validateReturnData: `assetClasses` must be non-empty');
  }
  const seen = new Set<string>();
  for (const ac of assetClasses) {
    if (seen.has(ac)) {
      throw new Error(`validateReturnData: duplicate asset class "${ac}"`);
    }
    seen.add(ac);
  }
  if (rows.length < 1) {
    throw new Error('validateReturnData: `rows` must have at least one historical period');
  }
  const width = assetClasses.length;
  for (let t = 0; t < rows.length; t++) {
    const row = rows[t] as readonly number[];
    if (row.length !== width) {
      throw new Error(
        `validateReturnData: rows[${t}] has ${row.length} entries, expected ${width} (== assetClasses.length)`,
      );
    }
  }
  if (periods !== undefined && periods.length !== rows.length) {
    throw new Error(
      `validateReturnData: periods.length (${periods.length}) !== rows.length (${rows.length})`,
    );
  }
  if (inflation !== undefined && inflation.length !== rows.length) {
    throw new Error(
      `validateReturnData: inflation.length (${inflation.length}) !== rows.length (${rows.length})`,
    );
  }
  if (fx !== undefined && fx.length !== rows.length) {
    throw new Error(
      `validateReturnData: fx.length (${fx.length}) !== rows.length (${rows.length})`,
    );
  }
  return data;
}

/**
 * Helper the matrix samplers share: turn a drawn historical row index `t` into the per-asset
 * decimal-return row used as one `MatrixReturn.byYear[y]`. Pure; bounds-checked. Exposed so
 * block / IID / historical samplers stay consistent.
 */
export function rowToMatrixYear(data: ReturnData, t: number): readonly number[] {
  const rows = data.rows;
  if (rows === undefined) {
    throw new Error('rowToMatrixYear: data has no `rows` cross-section');
  }
  if (!Number.isInteger(t) || t < 0 || t >= rows.length) {
    throw new RangeError(`rowToMatrixYear: row index ${t} out of range [0, ${rows.length})`);
  }
  return rows[t] as readonly number[];
}

/**
 * Assemble a {@link MatrixReturn} from `data.assetClasses` and an ordered list of drawn row
 * indices (one per simulated year), `byYear[y] = data.rows[draws[y]]`. The single place every
 * matrix sampler builds its `ReturnInput`, so the `assetClasses`/row-shape contract is uniform.
 * Pure; validating `draws.length >= horizon` is the caller's job.
 */
export function matrixFromDraws(data: ReturnData, draws: readonly number[]): MatrixReturn {
  const assetClasses = data.assetClasses;
  if (assetClasses === undefined) {
    throw new Error('matrixFromDraws: data has no `assetClasses` (multi-asset matrix required)');
  }
  const byYear = draws.map((t) => rowToMatrixYear(data, t));
  // ENRICHMENT: thread the per-draw realized inflation through to the ReturnInput so CPI-indexed
  // streams index off the SAME drawn historical year (the inflation column is aligned with `rows`).
  // Back-compat: when `data.inflation` is absent the field is simply omitted (cpi streams fall back
  // to flat), so every existing matrix sampler / stress test is byte-identical.
  const dataInflation = data.inflation;
  if (dataInflation === undefined) {
    return { kind: 'matrix', assetClasses, byYear };
  }
  const inflation = draws.map((t) => dataInflation[t] ?? 0);
  return { kind: 'matrix', assetClasses, byYear, inflation };
}
